/**
 * When an aeroplane in flight throws a shadow, where the shadow lands, and how
 * solid it is — the arithmetic, with no three.js in it, so every number below is
 * checkable without a GPU (`test/ground-shadow.test.ts`). `scene-shadows.ts` is
 * the half that turns these answers into geometry.
 *
 * ## This is an enhancement, and the fidelity question is settled
 *
 * **The original draws no shadow under an aeroplane in flight.** Checked against
 * this repo's own DOSBox-X captures of `RB.EXE` rather than assumed: `BARON.PAL`
 * 64 `#141414` is the colour the game's shadow art is made of, and counting
 * exact `#141414` pixels finds **none at all** in
 * `re-sessions/terrain/shots/alt-low-ext.png` (external view, 91 ft AGL over
 * Verdun), none in `alt-low-down.png` (the down view at the same 91 ft, a frame
 * that is two colours and nothing else, where a Camel's shadow would have
 * covered about a third of the width), none in
 * `docs/game/ui-reference/external-view.png`, and none in
 * `docs/formats/proof/terrain-original-verdun-low-91ft.png`. Every 640x400
 * capture in `re-sessions/` with a large index-64 count is a cockpit panel, a
 * menu, or the parked-plane viewer. "Shadows" is not one of the original's
 * detail options either — `docs/game/manual/04-gameplay.md` lists the whole
 * PREFERENCES panel and it is joystick, sound, mouse, time scale and two detail
 * sliders.
 *
 * The one shadow the original *does* draw is `PS.EXE`'s VIEW AIRPLANES screen,
 * whose `PTPLANE.TBL` slots 13-31 are nineteen flat plan-forms in that colour —
 * and `data/baked/world/PTPLANE.json` never places any of them, so they are the
 * viewer's own runtime art rather than world geometry. `planes/parked-aircraft.ts`
 * reproduces that screen; this module is not that screen.
 *
 * So the flight shadow rides **ENHANCED EFFECTS**, the port's existing toggle
 * for visual phenomena around an aeroplane that the original did not draw. Off,
 * a sortie's pixels are exactly what they were before this file existed.
 *
 * ## The sun it uses, and the floor under it
 *
 * `time-of-day.ts`'s `sunDirectionAt` — the mission's *own* sun, the one the sky
 * hangs `SUN.BMP` on — so the shadow and the disc that casts it cannot point
 * different ways. Two adjustments, both deliberate:
 *
 * - **No briefed hour means noon.** A dogfight, a duel and a `/vcr` replay brief
 *   no weather; `enhanced-sky-look.ts` already answers that with
 *   `DEFAULT_ENHANCED_HOUR`, and this reads the same constant rather than
 *   inventing a second default.
 * - **The elevation has a floor.** At dawn and dusk `CELESTIAL_HOURS` puts the
 *   sun about 7.5 degrees up, where `cot(7.5°)` is 7.6: an aeroplane 60 m up
 *   would throw its shadow **456 m** across the map, onto terrain it is not over,
 *   far outside the frame, attached to nothing the pilot can see. The floor is
 *   `PARKED_SUN_ELEVATION_DEG`, 25 degrees — the one shadow-casting sun this port
 *   has ever *measured* out of the original (`planes/parked-look.ts` recovers it
 *   off the viewer's own frame) — so no shadow here is ever thrown longer than
 *   the longest one the original is known to have drawn. It is a departure from
 *   the sky's own geometry and it is only ever a departure at dawn and dusk:
 *   `daytime` is within a thousandth of the zenith and passes through untouched.
 *
 * The *shading* sun (`three/ramp-shade.ts`'s horizontal `[0, 0, -1]`) is not used
 * and cannot be: a horizontal direction projects nothing onto the ground at all.
 * That is the trap the roadmap warns about, and it is the third distinct sun in
 * this codebase — see `planes/parked-aircraft.ts` for the same three.
 */

import { DEFAULT_ENHANCED_HOUR } from "./enhanced-sky-look.ts";
import { PARKED_SUN_ELEVATION_DEG } from "../planes/parked-look.ts";
import { sunDirectionAt } from "./time-of-day.ts";
import type { TimeOfDay, Vec3 } from "@red-opus/engine";

/**
 * Lowest sun the shadow projection will use, degrees above the horizon. See the
 * module doc: it is `planes/parked-look.ts`'s measured elevation, borrowed as a
 * floor rather than as the answer.
 */
export const SHADOW_SUN_FLOOR_DEG = PARKED_SUN_ELEVATION_DEG;

/**
 * At or below this height above the ground the shadow is drawn at full strength,
 * metres.
 *
 * **`SCRAMBLE_CLEARANCE_M`, borrowed rather than chosen**: 60 m is the engine's
 * own line for "off the ground" — what the take-off objective requires and a
 * little above where `autopilot.ts` hands a climbing-out AI back to ordinary
 * flying (`packages/engine`'s `generators-scramble.ts`). Everything the user
 * named is under it: taxiing, the take-off run and the first seconds of climb,
 * the approach and the flare, and a strafing pass, which in this sim is flown at
 * the height of the trains and sheds it is aimed at.
 *
 * Written as a literal here rather than imported, on purpose. The engine's
 * constant is a *mission rule* — change it and a scramble's objective changes —
 * and lessons.md's standing warning is that a fix to a shared quantity breaks
 * things that never mentioned it. A shadow's fade must not be able to move
 * because somebody retuned a take-off. `ground-shadow.test.ts` asserts the two
 * are equal, so the coupling is recorded and any future divergence is a
 * deliberate edit rather than a drift.
 */
export const SHADOW_FULL_BELOW_M = 60;

/**
 * Height above the ground at which the shadow has faded to nothing, metres.
 *
 * 300 m is about 1 000 ft, the height at which an aeroplane has left the
 * aerodrome and is simply flying. Patrol in this game is briefed at 6 000 to
 * 12 000 ft — twenty to forty times higher — so nothing on patrol is remotely
 * near this line, which is the "worth nothing at patrol height" half of the
 * request.
 *
 * The band it leaves is 240 m deep, and that depth is the point: a scout climbs
 * out at about 5 m/s, so the fade takes the better part of a minute and no frame
 * ever shows it move. A hard cut-off, or a band a tenth this deep, is the pop the
 * request asks not to have.
 *
 * Deliberately **not** `AI_TUNABLES.aiGroundClearanceM` (320 m, the AI's own
 * pull-out height), which is the nearest thing the engine has to a "low" line.
 * That number is marked CALIBRATION-PENDING and belongs to the combat lane; tying
 * a visual fade to it would mean a duel-census retune silently restyling every
 * take-off.
 */
export const SHADOW_GONE_ABOVE_M = 300;

/** A unit direction with a sign on its vertical component — what the projection needs of a sun. */
export type SunDirection = Vec3;

/**
 * The sun this mission's shadows are cast by, or `undefined` when there is none
 * to cast them.
 *
 * `undefined` means **night**, and it is the free half of "when not to draw":
 * `sunDirectionAt` already returns nothing once `sin(hourAngle) < 0`, which is
 * `sun.c`'s own night test. Nothing here has to know what night is.
 *
 * The degradation at dawn and dusk is continuous in the only sense that matters:
 * a mission's hour is one of four words fixed at briefing and cannot change
 * mid-sortie, so this is answered once per flight rather than crossed during one.
 */
export function shadowSunFor(timeOfDay: TimeOfDay | undefined): SunDirection | undefined {
  const sun = sunDirectionAt(timeOfDay ?? DEFAULT_ENHANCED_HOUR);
  return sun && liftedToFloor(sun);
}

/**
 * The same direction with its elevation raised to `SHADOW_SUN_FLOOR_DEG` if it
 * stands below it, keeping its bearing.
 *
 * A sun already at or above the floor is returned untouched — including the
 * degenerate overhead one, where there is no bearing to keep and none is needed.
 */
function liftedToFloor(sun: SunDirection): SunDirection {
  const floorY = Math.sin((SHADOW_SUN_FLOOR_DEG * Math.PI) / 180);
  if (sun.y >= floorY) return sun;
  const horizontal = Math.hypot(sun.x, sun.z);
  if (horizontal === 0) return sun;
  const scale = Math.cos((SHADOW_SUN_FLOOR_DEG * Math.PI) / 180) / horizontal;
  return { x: sun.x * scale, y: floorY, z: sun.z * scale };
}

/**
 * How solid the shadow is at `heightM` above the ground — 1 on the aerodrome,
 * 0 at and above `SHADOW_GONE_ABOVE_M`, linear between.
 *
 * Linear rather than eased because the band is 240 m deep: over that distance a
 * curve and a line are indistinguishable, and a line is the one a reader can
 * check against the two constants without running it.
 *
 * A negative height — an aeroplane briefly under the surface it is drawn on,
 * which a hard landing can produce for a tick — reads as full strength rather
 * than as an out-of-range value.
 */
export function shadowStrengthAt(heightM: number): number {
  if (heightM <= SHADOW_FULL_BELOW_M) return 1;
  if (heightM >= SHADOW_GONE_ABOVE_M) return 0;
  return (SHADOW_GONE_ABOVE_M - heightM) / (SHADOW_GONE_ABOVE_M - SHADOW_FULL_BELOW_M);
}

/** A plane to drop a shadow onto: a point it passes through and its upward normal. */
export interface GroundPlane {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Upward normal, not required to be unit length — every use below is a ratio. */
  readonly normalX: number;
  readonly normalY: number;
  readonly normalZ: number;
}

/** The flat plane at height `y` — what a region with no relief and every unit test wants. */
export function levelGroundPlane(x: number, y: number, z: number): GroundPlane {
  return { x, y, z, normalX: 0, normalY: 1, normalZ: 0 };
}

/**
 * The affine map that drops a point onto `plane` along `sun`, as the sixteen
 * **column-major** elements `THREE.Matrix4.fromArray` expects.
 *
 * The whole reason the shadow costs one matrix per aeroplane per frame instead
 * of a walk of its vertices: the projection is affine, so it composes with the
 * aeroplane's own world transform and the geometry underneath never has to be
 * touched. A point `p` lands at `p + t·s` where `t = n·(c − p) / (n·s)`, which
 * expands to a linear part `I − s nᵀ/(n·s)` and a translation `(n·c/(n·s))·s`.
 *
 * The result is rank-3 — it flattens space onto the plane — which is exactly
 * what a shadow is and is why `scene-shadows.ts` turns frustum culling off on
 * the meshes that carry it: a bounding sphere derived from a degenerate matrix
 * is not one to cull against.
 *
 * `undefined` when `n·s` is zero or nearly so, i.e. a sun in the plane of the
 * ground, whose shadow reaches infinity. `shadowSunFor`'s floor keeps a real
 * mission far away from that, so this is a guard rather than a case.
 */
export function groundProjectionMatrix(sun: SunDirection, plane: GroundPlane): number[] | undefined {
  const denominator = plane.normalX * sun.x + plane.normalY * sun.y + plane.normalZ * sun.z;
  if (Math.abs(denominator) < 1e-6) return undefined;
  const offset = (plane.normalX * plane.x + plane.normalY * plane.y + plane.normalZ * plane.z) / denominator;
  const nx = plane.normalX / denominator;
  const ny = plane.normalY / denominator;
  const nz = plane.normalZ / denominator;
  // Column-major: entries 0-3 are the image of x, 4-7 of y, 8-11 of z, 12-15 the translation.
  return [
    1 - sun.x * nx, -sun.y * nx, -sun.z * nx, 0,
    -sun.x * ny, 1 - sun.y * ny, -sun.z * ny, 0,
    -sun.x * nz, -sun.y * nz, 1 - sun.z * nz, 0,
    sun.x * offset, sun.y * offset, sun.z * offset, 1,
  ];
}

/**
 * Where a point at `(x, y, z)` puts its shadow on a **level** plane at height
 * `groundY` — the same answer `groundProjectionMatrix` gives, in the one case
 * worth having in closed form.
 *
 * `scene-shadows.ts` uses it to find the ground under the shadow rather than the
 * ground under the aeroplane, which are 129 m apart for an aeroplane 60 m up
 * under a floor-elevation sun and are the same point at noon.
 */
export function shadowGroundPoint(
  x: number,
  y: number,
  z: number,
  groundY: number,
  sun: SunDirection,
): { readonly x: number; readonly z: number } {
  if (Math.abs(sun.y) < 1e-6) return { x, z };
  const reach = (y - groundY) / sun.y;
  return { x: x - sun.x * reach, z: z - sun.z * reach };
}
